import { useState } from 'react';
import { Group, Text } from '@mantine/core';
import { IconReceiptOff } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { CustomerFilterSelect, ProductFilterSelect, SiteFilterSelect } from '../components/FilterSelects';
import { ReportPage } from '../components/ReportPage';
import { ReportTable, type ReportColumn } from '../components/ReportTable';
import { rangeQuery, useSalesReport } from '../api';
import { formatMoneyCents, formatQty } from '../format';
import type { SalesReportRow } from '../types';

export interface SalesReportProps {
  initialCustomerId?: string;
}

/** Report #7 (BUILD_SPEC.md §13) — sales grouped by customer/product/period.
 * Also the CUSTOMER search-result deep link target (`?customerId=`). */
export function SalesReport({ initialCustomerId }: SalesReportProps) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId ?? null);
  const [productId, setProductId] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);

  const query = useSalesReport({ siteId: siteId ?? undefined, customerId: customerId ?? undefined, productId: productId ?? undefined, from, to });
  const rows = query.data?.items ?? [];

  const totalMargin = rows.reduce((sum, r) => sum + BigInt(r.marginCents), 0n);

  const columns: ReportColumn<SalesReportRow>[] = [
    { key: 'customer', header: 'Customer', render: (r) => r.customerName },
    { key: 'product', header: 'Product', render: (r) => `${r.productSku} — ${r.productName}` },
    { key: 'qty', header: 'Qty', align: 'right', render: (r) => formatQty(r.qty) },
    { key: 'dispatches', header: 'Dispatches', align: 'right', render: (r) => String(r.dispatchCount) },
    { key: 'sale', header: 'Sale value', align: 'right', render: (r) => formatMoneyCents(r.saleValueCents) },
    { key: 'cost', header: 'Cost (WAC)', align: 'right', render: (r) => formatMoneyCents(r.costCents) },
    { key: 'margin', header: 'Margin', align: 'right', render: (r) => formatMoneyCents(r.marginCents) },
  ];

  return (
    <ReportPage
      title="Sales by customer/product"
      description="Posted dispatch value, cost (at relief WAC) and margin, grouped by customer and product."
      action={
        <CsvDownloadButton
          path="/reporting/sales"
          query={{
            siteId: siteId ?? undefined,
            customerId: customerId ?? undefined,
            productId: productId ?? undefined,
            ...rangeQuery(from, to),
          }}
          filename="sales-by-customer-product.csv"
          description="Exports this sales breakdown, matching your filters, as a CSV file"
        />
      }
    >
      <Group gap="md" wrap="wrap" align="flex-end">
        <SiteFilterSelect value={siteId} onChange={setSiteId} label="Site" w={200} />
        <CustomerFilterSelect value={customerId} onChange={setCustomerId} label="Customer" w={220} />
        <ProductFilterSelect value={productId} onChange={setProductId} label="Product" w={220} />
        <DateRangeFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      </Group>

      {rows.length > 0 && (
        <Text size="sm" c="dimmed">
          Total margin: <strong>{formatMoneyCents(totalMargin)}</strong> across {rows.length} row{rows.length === 1 ? '' : 's'}.
        </Text>
      )}

      <ReportTable
        columns={columns}
        rows={rows}
        getRowKey={(r) => `${r.customerId}:${r.productId}`}
        isLoading={query.isLoading}
        isError={query.isError}
        errorMessage="Failed to load the sales report."
        emptyMessage="No posted sales for the selected filters"
        emptyHint="Try a different customer, product or date range."
        emptyIcon={IconReceiptOff}
      />
    </ReportPage>
  );
}
